import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  File,
  Video,
  Presentation,
  ExternalLink,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { projectService } from '../..';
import { projectBoardService } from '../../api/boardApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../../constants/brandPalette';

const EMPTY_PRESENTABLE = {
  finalReport: [],
  onePager: [],
  presentation: [],
  videoDemo: [],
  pitchDeck: [],
  shortVideoDemo: [],
};

const resolveDocs = (ids, allDocs) =>
  (ids ?? [])
    .map(id => allDocs.find(d => String(d.id) === String(id)))
    .filter(Boolean);

// ─── File selection modal ─────────────────────────────────────────────────────

const FileSelectionModal = ({ isOpen, onClose, documents = [], linkedIds = [], onSelect }) => {
  if (!isOpen) return null;

  const available = documents.filter(d => !linkedIds.map(String).includes(String(d.id)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[85dvh] shadow-lg"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em]"
              style={{ color: BRAND_COLORS.primary }}
            >
              Project Files
            </p>
            <h2 className="text-base font-semibold mt-0.5" style={{ color: NEUTRAL_COLORS.black }}>
              Select File
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:opacity-70 transition-opacity"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4">
          {available.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <File className="w-8 h-8 mb-3" style={{ color: SYSTEM_COLORS.border }} />
              <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                {documents.length === 0 ? 'No project files available' : 'All files already linked'}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {available.map(doc => (
                <button
                  key={doc.id}
                  onClick={() => { onSelect(doc); onClose(); }}
                  className="w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-colors hover:opacity-80"
                  style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
                >
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
                  >
                    <File size={15} style={{ color: BRAND_COLORS.primary }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>
                      {doc.name}
                    </p>
                    {doc.description && (
                      <p className="text-xs truncate mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {doc.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-4 border-t flex-shrink-0 flex justify-end"
          style={{ borderColor: SYSTEM_COLORS.borderSoft }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold border hover:opacity-70 transition-opacity"
            style={{
              borderColor: SYSTEM_COLORS.border,
              color: SYSTEM_COLORS.textMuted,
              backgroundColor: SYSTEM_COLORS.shellSurface,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Document link card ───────────────────────────────────────────────────────

const DocumentLink = ({ title, icon: Icon, linkedIds, allDocuments, onRemove, onAdd, section }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const resolvedFiles = resolveDocs(linkedIds, allDocuments);

  const handleFileClick = (file) => {
    const url = file.url || file.fileUrl;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('File URL not found');
    }
  };

  return (
    <div
      className="rounded-xl border shadow-md overflow-hidden"
      style={{
        backgroundColor: SYSTEM_COLORS.shellSurface,
        borderColor: SYSTEM_COLORS.borderSoft,
      }}
    >
      {/* Card header */}
      <div
        className="flex items-center gap-2.5 px-4 py-3 border-b"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
      >
        <div
          className="flex items-center justify-center w-7 h-7 rounded-lg"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <Icon className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
        </div>
        <h3 className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>{title}</h3>
        <span
          className="ml-auto text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface, color: SYSTEM_COLORS.textMuted }}
        >
          {resolvedFiles.length}
        </span>
      </div>

      {/* File rows */}
      <div className="p-3 space-y-2">
        {resolvedFiles.map((file) => (
          <div
            key={file.id}
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
          >
            <File className="w-4 h-4 flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
            <span
              className="text-sm flex-1 min-w-0 truncate"
              style={{ color: NEUTRAL_COLORS.black }}
            >
              {file.name}
            </span>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => handleFileClick(file)}
                className="p-1.5 rounded-lg hover:opacity-70 transition-opacity"
                style={{ color: BRAND_COLORS.primary }}
                title={file.url ? 'Open link' : 'Open file'}
              >
                {file.url ? <ExternalLink className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => onRemove(section, file.id)}
                className="p-1.5 rounded-lg hover:opacity-70 transition-opacity"
                style={{ color: SYSTEM_COLORS.danger }}
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-1.5 text-sm font-semibold mt-1 px-1 hover:opacity-70 transition-opacity"
          style={{ color: BRAND_COLORS.primary }}
        >
          <Plus className="w-4 h-4" />
          Add File
        </button>
      </div>

      <FileSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        documents={allDocuments}
        linkedIds={linkedIds}
        onSelect={(file) => {
          onAdd(section, file);
          setIsModalOpen(false);
        }}
      />
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const Presentable = ({ projectId }) => {
  const [linkedFiles, setLinkedFiles] = useState(EMPTY_PRESENTABLE);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const savePresentable = useCallback(async (updatedFiles) => {
    await projectService.updateProjectPresentable(projectId, updatedFiles);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [project, docs] = await Promise.all([
          projectService.getProject(projectId),
          projectBoardService.getProjectDocuments(projectId),
        ]);

        if (cancelled) return;

        const presentableData = project?.presentable && typeof project.presentable === 'object'
          ? project.presentable
          : {};
        setLinkedFiles({ ...EMPTY_PRESENTABLE, ...presentableData });
        setProjectDocuments(Array.isArray(docs) ? docs : []);
      } catch {
        if (!cancelled) toast.error('Failed to load presentable files');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId]);

  const handleFileSelected = async (section, file) => {
    const docId = String(file.id);
    if ((linkedFiles[section] ?? []).map(String).includes(docId)) return;

    const updatedFiles = {
      ...linkedFiles,
      [section]: [...(linkedFiles[section] ?? []), docId],
    };
    setLinkedFiles(updatedFiles);
    try {
      await savePresentable(updatedFiles);
      toast.success('File linked successfully');
    } catch {
      setLinkedFiles(prev => prev);
      toast.error('Failed to link file');
    }
  };

  const handleRemoveFile = async (section, fileId) => {
    const updatedFiles = {
      ...linkedFiles,
      [section]: (linkedFiles[section] ?? []).filter(id => String(id) !== String(fileId)),
    };
    setLinkedFiles(updatedFiles);
    try {
      await savePresentable(updatedFiles);
      toast.success('File removed');
    } catch {
      setLinkedFiles(prev => prev);
      toast.error('Failed to remove file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  const sectionProps = (section) => ({
    allDocuments: projectDocuments,
    linkedIds: linkedFiles[section] ?? [],
    onRemove: handleRemoveFile,
    onAdd: handleFileSelected,
    section,
  });

  return (
    <div className="max-w-4xl mx-auto space-y-8">

      {/* Main Documentation */}
      <div>
        <p
          className="text-xs font-semibold uppercase tracking-[0.18em] mb-1"
          style={{ color: BRAND_COLORS.primary }}
        >
          Deliverables
        </p>
        <h2 className="text-lg font-bold mb-4" style={{ color: NEUTRAL_COLORS.black }}>
          Main Documentation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DocumentLink title="Final Project Report" icon={FileText} {...sectionProps('finalReport')} />
          <DocumentLink title="One Pager" icon={File} {...sectionProps('onePager')} />
        </div>
      </div>

      {/* Presentations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <p
            className="text-xs font-semibold uppercase tracking-[0.18em] mb-1"
            style={{ color: BRAND_COLORS.primary }}
          >
            Showcase
          </p>
          <h2 className="text-lg font-bold mb-4" style={{ color: NEUTRAL_COLORS.black }}>
            Normal Presentations
          </h2>
          <div className="space-y-3">
            <DocumentLink title="PPT Presentation" icon={Presentation} {...sectionProps('presentation')} />
            <DocumentLink title="Video Demo" icon={Video} {...sectionProps('videoDemo')} />
          </div>
        </div>

        <div>
          <p
            className="text-xs font-semibold uppercase tracking-[0.18em] mb-1"
            style={{ color: BRAND_COLORS.primary }}
          >
            Investor Ready
          </p>
          <h2 className="text-lg font-bold mb-4" style={{ color: NEUTRAL_COLORS.black }}>
            Pitch Materials
          </h2>
          <div className="space-y-3">
            <DocumentLink title="PPT Pitch Deck" icon={Presentation} {...sectionProps('pitchDeck')} />
            <DocumentLink title="Short Video Demo" icon={Video} {...sectionProps('shortVideoDemo')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Presentable;
